/**
 * Inputs: adult/child fare counts and integer minor units.
 * Outputs: derived group total. Free children add nothing. Never invent a split.
 */

import type { Booking } from "./schema.ts";
import { minorFactor } from "./money.ts";

export type PriceBasis = NonNullable<Booking["priceBasis"]>;

export function priceBasisTotalMinor(basis: Pick<PriceBasis, "adultCount" | "adultAmountMinor" | "paidChildCount" | "childAmountMinor">): number {
  return basis.adultCount * basis.adultAmountMinor + basis.paidChildCount * basis.childAmountMinor;
}

export function priceBasisSummary(basis: PriceBasis, currency: string): string {
  const adult = `${basis.adultCount} adult${basis.adultCount === 1 ? "" : "s"} × ${majorLabel(currency, basis.adultAmountMinor)}`;
  const paid = basis.paidChildCount
    ? ` · ${basis.paidChildCount} child × ${majorLabel(currency, basis.childAmountMinor)}`
    : "";
  const free = basis.freeChildCount
    ? ` · ${basis.freeChildCount} free child${basis.freeChildCount === 1 ? "" : "ren"}`
    : "";
  return `${adult}${paid}${free}`;
}

export function majorLabel(currency: string, amountMinor: number): string {
  const factor = minorFactor(currency);
  const major = amountMinor / factor;
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
}

export function parseMajorInput(raw: string, currency: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * minorFactor(currency));
}

export function majorInputFromMinor(amountMinor: number, currency: string): string {
  return String(amountMinor / minorFactor(currency));
}

export function parseCount(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

export function buildPriceBasis(input: {
  adultCount: string;
  adultAmount: string;
  paidChildCount: string;
  childAmount: string;
  freeChildCount: string;
  ageException: string;
  currency: string;
}): { basis: PriceBasis; error: string | null } {
  const adultCount = parseCount(input.adultCount);
  const paidChildCount = parseCount(input.paidChildCount);
  const freeChildCount = parseCount(input.freeChildCount);
  const adultAmountMinor = parseMajorInput(input.adultAmount, input.currency);
  const childAmountMinor = parseMajorInput(input.childAmount, input.currency);
  if (adultCount == null || paidChildCount == null || freeChildCount == null) {
    return { basis: emptyBasis(), error: "People counts must be whole numbers." };
  }
  if (adultAmountMinor == null || childAmountMinor == null) {
    return { basis: emptyBasis(), error: "Enter fares as amounts, for example 1850." };
  }
  return {
    basis: {
      adultCount,
      adultAmountMinor,
      paidChildCount,
      childAmountMinor,
      freeChildCount,
      ageException: input.ageException.trim() || undefined,
    },
    error: null,
  };
}

function emptyBasis(): PriceBasis {
  return {
    adultCount: 0,
    adultAmountMinor: 0,
    paidChildCount: 0,
    childAmountMinor: 0,
    freeChildCount: 0,
  };
}
